/* Admin: corrections per day/month and per team member for a chosen range (default last month) — spot repeat mistakes. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ymd = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const back = (months) => { const d = new Date(); d.setMonth(d.getMonth() - months); return ymd(d); };
const PRESETS = [["1M", 1], ["3M", 3], ["6M", 6], ["12M", 12]];

const People = ({ rows }) => (
  <table className="w-full text-xs" data-testid="corr-trend-people">
    <thead><tr className="border-b text-left text-slate-500"><th className="py-1">Team member</th><th className="text-right">Corrections</th><th className="text-right">Invoices</th><th className="w-1/3"></th></tr></thead>
    <tbody>{rows.map((p) => (
      <tr key={p.name} className="border-b last:border-0" data-testid={`corr-trend-person-${p.name}`}>
        <td className="py-1">{p.name}</td><td className="text-right font-semibold">{p.count}</td><td className="text-right text-slate-500">{p.invoices}</td>
        <td className="pl-2"><div className="h-2 rounded bg-red-500" style={{ width: `${(p.count / rows[0].count) * 100}%` }} /></td>
      </tr>))}
      {!rows.length && <tr><td colSpan={4} className="py-3 text-center text-slate-400">No corrections in this range</td></tr>}</tbody>
  </table>
);

export const CorrectionTrendButton = () => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(back(1));
  const [end, setEnd] = useState(ymd(new Date()));
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  let isAdmin = false;
  try { isAdmin = JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { /* not logged in */ }
  const load = useCallback(async () => {
    setBusy(true);
    try { setD((await axios.get(`${API}/order-register/corrections/trend`, { headers: getAuthHeader(), params: { start, end } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load the correction trend"); }
    setBusy(false);
  }, [start, end]);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!isAdmin) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Corrections per month and per team member"
        className="inline-flex h-7 items-center gap-1 rounded border px-1.5 text-[11px] font-semibold text-red-700 hover:bg-red-50" data-testid="or-correction-trend-btn">
        <BarChart3 className="h-3.5 w-3.5" /> Trend</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="corr-trend-dialog">
          <DialogHeader><DialogTitle className="text-base">Correction trend</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="h-7 rounded border px-1" data-testid="corr-trend-start" />
            <span>to</span>
            <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="h-7 rounded border px-1" data-testid="corr-trend-end" />
            {PRESETS.map(([l, m]) => (
              <button key={l} type="button" onClick={() => { setStart(back(m)); setEnd(ymd(new Date())); }}
                className="rounded-full border px-2 py-0.5 hover:bg-slate-100" data-testid={`corr-trend-preset-${l}`}>{l}</button>))}
            {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
            {d && <span className="ml-auto font-semibold text-red-700" data-testid="corr-trend-total">{d.total} correction{d.total === 1 ? "" : "s"}</span>}
          </div>
          {d && (
            <div className="space-y-4">
              <div className="h-48" data-testid="corr-trend-chart">
                <p className="mb-1 text-[11px] font-semibold text-slate-500">Per {d.bucket}</p>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.series} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} tickFormatter={(v) => (d.bucket === "day" ? v.slice(5) : v)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="count" name="Corrections" fill="#dc2626" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <People rows={d.by_user} />
              {d.repeat_invoices.length > 0 && (
                <div data-testid="corr-trend-repeats">
                  <p className="mb-1 text-[11px] font-semibold text-slate-500">Invoices corrected more than once</p>
                  <div className="flex flex-wrap gap-1">{d.repeat_invoices.map((r) => (
                    <span key={r.invoice_key} className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px]" title={r.people.join(", ")}>
                      <b className="font-mono">{r.invoice_key}</b> ×{r.count}</span>))}</div>
                </div>)}
            </div>)}
        </DialogContent>
      </Dialog>
    </>
  );
};
